import { cva, type VariantProps } from 'class-variance-authority';
import type { HTMLAttributes, Ref } from 'react';
import { cn } from '@/lib/utils';

const alertVariants = cva(
  'relative w-full rounded-lg border p-4 [&>svg~*]:pl-7 [&>svg+div]:translate-y-[-3px] [&>svg]:absolute [&>svg]:left-4 [&>svg]:top-4 [&>svg]:text-foreground',
  {
    variants: {
      variant: {
        default: 'border border-border/60 bg-card text-card-foreground',
        destructive: 'border border-destructive/50 bg-destructive/10 text-destructive [&>svg]:text-destructive',
        warning: 'border border-warning/40 bg-warning/10 text-warning-foreground [&>svg]:text-warning-foreground',
        error: 'border border-destructive/40 bg-destructive/10 text-destructive [&>svg]:text-destructive',
        success: 'border border-success/40 bg-success/10 text-success-foreground [&>svg]:text-success-foreground',
        info: 'border border-info/40 bg-info/10 text-info-foreground [&>svg]:text-info-foreground',
      },
    },
    defaultVariants: {
      variant: 'default',
    },
  }
);

type BaseAlertProperties<TElement extends HTMLElement> = HTMLAttributes<TElement> & {
  ref?: Ref<TElement> | null;
};

type AlertProperties = BaseAlertProperties<HTMLDivElement> & VariantProps<typeof alertVariants>;

const Alert = ({ className, variant, ref, ...properties }: AlertProperties) => (
  <div ref={ref} role="alert" className={cn(alertVariants({ variant }), className)} {...properties} />
);

type AlertTitleProperties = BaseAlertProperties<HTMLHeadingElement>;

const AlertTitle = ({ className, ref, ...properties }: AlertTitleProperties) => (
  <h5 ref={ref} className={cn('mb-1 font-medium leading-none tracking-tight', className)} {...properties} />
);

type AlertDescriptionProperties = BaseAlertProperties<HTMLParagraphElement>;

const AlertDescription = ({ className, ref, ...properties }: AlertDescriptionProperties) => (
  <div ref={ref} className={cn('text-sm [&_p]:leading-relaxed', className)} {...properties} />
);

export { Alert, AlertTitle, AlertDescription };
